/**
 * The single in-memory answer to "does this company still owe review decisions?"
 *
 * WHY THIS EXISTS. PipelineReviewGate and CompanyPipelineReviewPage both need
 * that answer, and they used to derive it independently: the gate cached a
 * boolean from its own fetch, the page fetched its own queue. That deadlocks.
 * The gate skips re-fetching while the user is on the review page (the page
 * owns the list there), so its boolean stays `true` for the whole session. The
 * moment the page finished the queue and navigated to /company/pipeline, the
 * gate's stale `true` bounced the user straight back, and the corrective fetch
 * was cancelled by that same redirect. Infinite loop, reported 2026-08-27.
 *
 * The fix is not "re-fetch harder" — any re-fetch races the redirect. It is to
 * remove the second derivation: whoever last heard from the server PUBLISHES
 * the count here, synchronously, and the gate reads it. The page publishes 0
 * BEFORE it navigates, so by the time the gate evaluates, there is nothing to
 * block on. No race, because no fetch is involved in the handoff.
 *
 * `null` means "nobody has asked the server yet in this session" — the gate
 * treats that as "unknown, go find out", never as "blocked". Module state, so
 * it resets on a full page load, which is exactly when a fresh check is wanted.
 * A negotiation that goes stale mid-session therefore does not block the user
 * until their next reload. That is deliberate: being interrupted by a wall
 * mid-task is worse than being asked a few minutes later.
 */

let knownTotal = null;
let knownGateEnabled = true;
const listeners = new Set();

/**
 * Record what the server just said. Call this from anywhere that receives a
 * review-queue response, and from the review page after each decision.
 *
 * @param {number} total Outstanding decisions remaining.
 * @param {boolean} [gateEnabled] Server-side kill switch, when known.
 */
export function publishReviewQueueTotal(total, gateEnabled) {
  knownTotal = Number.isFinite(total) ? total : 0;
  if (typeof gateEnabled === 'boolean') knownGateEnabled = gateEnabled;
  listeners.forEach((fn) => fn({ total: knownTotal, gateEnabled: knownGateEnabled }));
}

/** `{ total: number | null, gateEnabled: boolean }`. total null = not yet known. */
export function getReviewQueueState() {
  return { total: knownTotal, gateEnabled: knownGateEnabled };
}

export function subscribeReviewQueue(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Test seam. Never call from app code. */
export function __resetReviewQueueSignal() {
  knownTotal = null;
  knownGateEnabled = true;
  listeners.clear();
}
